import type { CopilotJsonObject } from '@customerio/hydra-copilot';
import { useCopilotTool } from '@customerio/hydra-copilot/react';

import { sentryCaptureException } from '@/utils/sentry';
import { useEffect, useRef } from 'react';
import { z } from '@customerio/hydra-validation';

import { AskUserQuestion } from './ask-user-question';
import { AskUserQuestionError } from './ask-user-question-error';

/**
 * The UI always appends its own free-text "Something else" option, so any catch-all
 * option the model supplies itself would render as a duplicate (e.g. two "Something
 * else" rows). Matching labels are dropped during parsing.
 */
const ESCAPE_HATCH_LABEL =
  /^(something else|other|none\s+of\s+(the\s+)?(above|these|them)|let me specify|i'?ll specify)$/i;

const optionSchema = z.object({
  label: z.string().describe('Display text for this option (1-5 words, concise).'),
  description: z.string().describe('Explanation of what this option means or what will happen if chosen.'),
});

const questionSchema = z.object({
  question: z
    .string()
    .describe(
      'The complete question to ask the user. Should be clear, specific, and end with a question mark. If multiSelect is true, phrase it accordingly.',
    ),
  multiSelect: z
    .boolean()
    .describe('Set to true to allow the user to select multiple options. Use when choices are not mutually exclusive.'),
  options: z
    .array(optionSchema)
    .min(2)
    .max(4)
    .describe(
      'The available choices (2-4 options). Each should be distinct and concrete. Do not include a catch-all/fallback option such as "Something else", "Other", "None of these", or "A different …" — a free-text "Something else" option is added automatically.',
    ),
});

const requestSchema = z.object({
  questions: z.array(questionSchema).min(1).max(4).describe('Questions to ask the user (1-4 questions).'),
});

export type AskUserQuestionArgs = z.infer<typeof requestSchema>;

/**
 * Validates and parses the raw `args` from the LLM into typed
 * `AskUserQuestionArgs`. Handles cases where:
 * - `args.questions` is a JSON string instead of an array (LLM quirk)
 * - `args.questions` is malformed JSON (unescaped quotes, truncated, etc.)
 * - `args.questions` has the wrong shape (missing fields, wrong types)
 *
 * Returns `{ data }` on success or `{ error }` on failure.
 */
export function parseArgs(
  args: CopilotJsonObject,
): { data: AskUserQuestionArgs; error?: never } | { data?: never; error: string } {
  let questions = args.questions;

  // The LLM sometimes sends `questions` as a JSON string instead of an array
  if (typeof questions === 'string') {
    try {
      questions = JSON.parse(questions);
    } catch {
      return { error: "The Agent's response couldn't be read." };
    }
  }

  if (!Array.isArray(questions)) {
    return { error: 'Expected questions to be an array.' };
  }

  if (questions.length === 0) {
    return { error: 'No questions were provided.' };
  }

  // Validate each question has the minimum required fields
  // and build sanitized copies to avoid mutating the caller's data.
  const sanitized = [];
  for (let index = 0; index < questions.length; index++) {
    const q = questions[index];
    if (q == null || typeof q !== 'object') {
      return { error: `Question ${index + 1} is not a valid object.` };
    }
    if (Array.isArray(q) || typeof q.question !== 'string') {
      return { error: `Question ${index + 1} is missing a question text.` };
    }
    const options = q.options;
    if (!Array.isArray(options) || options.length === 0) {
      return { error: `Question ${index + 1} has no options.` };
    }
    const sanitizedOptions = [];
    for (let optionIndex = 0; optionIndex < options.length; optionIndex++) {
      const opt = options[optionIndex];
      if (opt == null || typeof opt !== 'object' || Array.isArray(opt) || typeof opt.label !== 'string') {
        return { error: `Question ${index + 1}, option ${optionIndex + 1} is missing a label.` };
      }
      // Drop catch-all options the model added itself — the UI always renders its own
      // free-text "Something else" option, so these would show up as duplicates.
      if (ESCAPE_HATCH_LABEL.test(opt.label.trim())) {
        continue;
      }
      sanitizedOptions.push({
        label: opt.label,
        description: typeof opt.description === 'string' ? opt.description : '',
      });
    }
    if (sanitizedOptions.length === 0) {
      return { error: `Question ${index + 1} has no concrete options.` };
    }
    sanitized.push({
      question: q.question,
      multiSelect: typeof q.multiSelect === 'boolean' ? q.multiSelect : false,
      options: sanitizedOptions,
    });
  }

  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return { data: { questions: sanitized } as AskUserQuestionArgs };
}

export function AskUserQuestionTool() {
  useCopilotTool({
    type: 'client',
    name: 'ask_user_question',
    humanInTheLoop: true,
    description: `
      Use this tool when you need to ask the user questions during the conversation.

      This allows you to:
      1. Gather user preferences or requirements
      2. Clarify ambiguous instructions
      3. Get decisions on implementation choices
      4. Offer choices to the user about what direction to take

      IMPORTANT: You MUST always provide meaningful, specific options for every question.
      Never ask a question where the only reasonable answer is "Something else" — always
      provide at least 2 concrete options that cover the most likely answers.

      A free-text "Something else" option is automatically added to every question. Do NOT
      include any catch-all, fallback, or escape-hatch option yourself — for example
      "Something else", "Other", "None of these", "A different …", or "Let me specify".
      Such options are redundant and will render as duplicates. Every option you provide
      must be a concrete, specific choice.

      Use multiSelect: true when the user can pick more than one answer.

      <sample-chat>
      User: Help me set up email authentication
      Model: ask_user_question({ questions: [{ question: "Which authentication method would you like to configure?", multiSelect: false, options: [{ label: "SPF", description: "Sender Policy Framework — specify which servers can send email for your domain" }, { label: "DKIM", description: "DomainKeys Identified Mail — add a cryptographic signature to outgoing messages" }, { label: "DMARC", description: "Domain-based Message Authentication — set policies for handling unauthenticated mail" }] }] })
      </sample-chat>

      <sample-chat>
      User: I want to create a new campaign
      Model: ask_user_question({ questions: [{ question: "What type of campaign would you like to create?", multiSelect: false, options: [{ label: "Newsletter", description: "A one-time email sent to a segment of your audience" }, { label: "Automated", description: "A triggered campaign that sends based on user behavior or attributes" }] }] })
      </sample-chat>
    `,
    requestSchema: z.toJSONSchema(requestSchema),
    render({ args, status, response, respond }) {
      const answerText = typeof response?.result === 'string' ? response.result : undefined;

      const parsed = parseArgs(args);
      if (parsed.error != null) {
        return (
          <AskUserQuestionParseError
            error={parsed.error}
            args={args}
            onContinue={
              status === 'executing' && respond != null
                ? () =>
                    respond(
                      'The question could not be displayed due to a formatting error. Please continue without asking questions.',
                    )
                : undefined
            }
          />
        );
      }

      return (
        <AskUserQuestion
          args={parsed.data}
          status={status}
          answerText={answerText}
          onAnswer={(answer) => respond?.(answer)}
        />
      );
    },
  });

  return null;
}

/**
 * Wraps `AskUserQuestionError` and reports the parse failure to Sentry
 * exactly once via `useEffect`, avoiding the side-effect-in-render trap
 * that would fire on every re-render.
 */

type AskUserQuestionParseErrorProps = {
  error: string;
  args: Record<string, unknown>;
  onContinue?: () => void;
};

function AskUserQuestionParseError({ error, args, onContinue }: AskUserQuestionParseErrorProps) {
  const reportedRef = useRef(false);

  useEffect(() => {
    if (!reportedRef.current) {
      reportedRef.current = true;
      sentryCaptureException(new Error(`ask_user_question: ${error}`), {
        extra: { args },
      });
    }
  }, [error, args]);

  return <AskUserQuestionError error={error} onContinue={onContinue} />;
}
